/* Feb-2026 · End-User License Agreement — discreet bottom banner.
 *
 * REDESIGN (Feb-2026): The old full-screen modal locked users on a
 * single screen with a scroll-to-bottom requirement. This blocked
 * page interaction and reads of the underlying app. The new design:
 *
 *   • A small fixed-bottom banner (cookie-style) with a checkbox
 *     and Accept button — user can tick and dismiss inline.
 *   • A "Read agreement" button opens an optional Dialog with the
 *     full markdown for anyone who wants to read it.
 *   • No scroll gate — checking the box is sufficient consent
 *     (audit trail still stores click+timestamp+IP).
 *   • Page content underneath remains scrollable and interactive.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2, Download, ShieldCheck, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth_context';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

// Session-scoped dismiss so a user who chose "Read later" isn't
// re-nagged on every route change. Cleared on refresh.
const DEFER_KEY = 'pw_eula_deferred_v1';

// Ultra-lightweight markdown renderer for the Read dialog.
function renderMd(text) {
  const lines = (text || '').split('\n');
  const out = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      out.push(<ul key={`ul-${out.length}`} className="my-2 ml-5 list-disc space-y-1 text-[12.5px]">{list}</ul>);
      list = [];
    }
  };
  const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith('**') && seg.endsWith('**')
      ? <strong key={i}>{seg.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{seg}</React.Fragment>);
  lines.forEach((l, idx) => {
    if (l.startsWith('# ')) { flushList(); out.push(<h1 key={idx} className="mt-4 text-[17px] font-bold text-primary">{bold(l.slice(2))}</h1>); }
    else if (l.startsWith('## ')) { flushList(); out.push(<h2 key={idx} className="mt-4 text-[14px] font-bold text-primary">{bold(l.slice(3))}</h2>); }
    else if (l.startsWith('### ')) { flushList(); out.push(<h3 key={idx} className="mt-3 text-[13px] font-semibold">{bold(l.slice(4))}</h3>); }
    else if (l.startsWith('- ') || /^\s{2,}- /.test(l)) { list.push(<li key={idx}>{bold(l.replace(/^\s*-\s+/, ''))}</li>); }
    else if (l.trim() === '---') { flushList(); out.push(<hr key={idx} className="my-3 border-neutral-300" />); }
    else if (l.trim() === '') { flushList(); }
    else { flushList(); out.push(<p key={idx} className="my-2 text-[12.5px] leading-relaxed">{bold(l)}</p>); }
  });
  flushList();
  return out;
}

export default function EulaGate() {
  const { t } = useTranslation();
  const { user, checking } = useAuth();
  const [status,    setStatus]    = useState(null);
  const [eulaDoc,   setEulaDoc]   = useState(null);
  const [checked,   setChecked]   = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [readOpen,  setReadOpen]  = useState(false);
  const [deferred,  setDeferred]  = useState(() => {
    try { return sessionStorage.getItem(DEFER_KEY) === '1'; } catch { return false; }
  });

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [s, d] = await Promise.all([
        http.get('/eula/status').then((r) => r.data),
        http.get('/eula/current').then((r) => r.data).catch(() => null),
      ]);
      setStatus(s);
      setEulaDoc(d);
    } catch {
      // If the gate itself is unreachable, don't block the user.
      setStatus({ requires_acceptance: false, current_version: null });
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Reset checkbox when the doc version changes.
  useEffect(() => { setChecked(false); }, [eulaDoc?.version]);

  const accept = async () => {
    if (!eulaDoc) return;
    setAccepting(true);
    try {
      await http.post('/eula/accept', {
        version: eulaDoc.version,
        method:  'click',
      });
      toast.success(t('eula.acceptedToast'));
      const s = await http.get('/eula/status').then((r) => r.data);
      setStatus(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('eula.acceptFailedToast'));
    } finally { setAccepting(false); }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = `${API}/eula/download.txt`;
    a.download = `crown-and-rose-eula.txt`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const dismiss = () => {
    setDeferred(true);
    try { sessionStorage.setItem(DEFER_KEY, '1'); } catch { /* private mode */ }
  };

  // Nothing to gate if auth still loading, no user, no doc yet,
  // acceptance not required, or user deferred this session.
  if (checking || !user || !status || !status.requires_acceptance) return null;

  return (
    <>
      {/* Discreet bottom banner — page underneath stays scrollable */}
      {!deferred && (
        <div
          className="fixed inset-x-0 bottom-0 z-[9500] flex justify-center px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
          data-testid="eula-gate"
          role="region"
          aria-label="End-user license agreement"
        >
          <div
            className="pointer-events-auto flex w-full max-w-3xl flex-wrap items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-[12.5px] shadow-panel-active sm:flex-nowrap"
            data-testid="eula-banner"
          >
            <ShieldCheck className="h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold" data-testid="eula-title">
                {eulaDoc?.title || t('eula.defaultTitle')}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {t('eula.agreeToTerms')}{' '}
                <button
                  type="button"
                  onClick={() => setReadOpen(true)}
                  className="underline underline-offset-2 hover:text-primary"
                  data-testid="eula-read-btn"
                >
                  {t('eula.readAgreement')}
                </button>
                {eulaDoc?.version && (
                  <span className="ml-2 text-muted-foreground/70">v{eulaDoc.version}</span>
                )}
              </div>
            </div>
            <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                data-testid="eula-checkbox"
                className="h-3.5 w-3.5"
              />
              <span>{t('eula.iAgree')}</span>
            </label>
            <button
              type="button"
              onClick={accept}
              disabled={!checked || accepting}
              data-testid="eula-accept"
              className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
              {t('eula.accept')}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="eula-defer"
              aria-label={t('eula.dismissAria')}
              className="shrink-0 rounded-sm p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Optional full-text dialog — opens on "Read agreement" */}
      <Dialog open={readOpen} onOpenChange={setReadOpen}>
        <DialogContent className="max-w-3xl overflow-hidden p-0" data-testid="eula-read-dialog">
          <DialogHeader className="border-b border-border bg-panel-header/40 px-5 py-3">
            <DialogTitle className="flex items-center gap-2 text-[14px]">
              <ShieldCheck className="h-4 w-4 text-primary" />
              {eulaDoc?.title || t('eula.defaultTitle')}
            </DialogTitle>
            {eulaDoc?.version && (
              <DialogDescription className="text-[11px]">{t('eula.versionLabel', { version: eulaDoc.version })}</DialogDescription>
            )}
          </DialogHeader>
          <div
            data-testid="eula-scroll"
            className="max-h-[65vh] overflow-y-auto px-6 py-4 text-foreground"
          >
            {eulaDoc?.markdown
              ? renderMd(eulaDoc.markdown)
              : <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('eula.loadingAgreement')}
                </div>}
          </div>
          <DialogFooter className="flex-wrap items-center gap-2 border-t border-border bg-card px-5 py-3">
            <button
              type="button"
              onClick={download}
              disabled={!eulaDoc}
              data-testid="eula-download"
              className="rounded-sm border border-border bg-background px-3 py-1.5 text-[11.5px] hover:bg-secondary"
            >
              <Download className="mr-1 inline h-3 w-3" /> {t('eula.download')}
            </button>
            <label className="ml-auto flex items-center gap-1.5 text-[12px]">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                data-testid="eula-read-checkbox"
                className="h-3.5 w-3.5"
              />
              <span>{t('eula.iAgree')}</span>
            </label>
            <button
              type="button"
              onClick={async () => { await accept(); setReadOpen(false); }}
              disabled={!checked || accepting}
              data-testid="eula-read-accept"
              className="inline-flex items-center gap-1 rounded-sm bg-primary px-4 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {accepting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
              {t('eula.accept')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
